/**
 * Unified Language & Currency Selector — Global Premium
 * Rined popover with luxury styling
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Globe } from 'lucide-react';
import { CURRENCIES } from '../../data/currencies';

const LANGUAGES = [
    { code: 'th', label: 'ไทย', flag: '🇹🇭' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export function LanguageCurrencySelector({
    currentLang,
    onLangChange,
    currentCurrency,
    onCurrencyChange,
    t
}) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedLang = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];
    const selectedCurrency = CURRENCIES[currentCurrency] || CURRENCIES.thb;

    return (
        <div className="relative" ref={containerRef}>
            {/* Trigger Button - Premium minimal */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-full transition-all duration-300 border ${
                    isOpen
                        ? 'bg-luxury-charcoal text-white border-luxury-charcoal shadow-luxury'
                        : 'bg-white/60 hover:bg-white text-neutral-600 border-neutral-200/80 hover:border-neutral-300 hover:shadow-soft'
                }`}
            >
                <span className="text-base leading-none filter drop-shadow-sm">{selectedLang.flag}</span>
                <div className="h-3 w-px bg-current opacity-20 mx-0.5" />
                <span className="text-[11px] font-semibold tracking-wide">
                    {selectedCurrency.code}
                </span>
                <ChevronDown size={12} className={`opacity-50 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Panel - Premium Popover */}
            {isOpen && (
                <div className="absolute top-full right-0 mt-3 w-68 bg-white/95 backdrop-blur-2xl rounded-2xl shadow-elevated border border-white/60 p-3 z-50 animate-scaleIn origin-top-right">

                    {/* Language Section */}
                    <div className="p-2">
                        <div className="flex items-center gap-2 mb-3 px-2">
                            <Globe size={11} className="text-neutral-400" />
                            <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-bold font-body">
                                {t ? t('language') : 'Language'}
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            {LANGUAGES.map(lang => (
                                <button
                                    key={lang.code}
                                    onClick={() => { onLangChange(lang.code); }}
                                    className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-[13px] transition-all duration-300 ${
                                        currentLang === lang.code
                                            ? 'bg-luxury-charcoal text-white shadow-luxury'
                                            : 'text-neutral-600 hover:bg-neutral-50 hover:text-luxury-charcoal'
                                    }`}
                                >
                                    <span className="flex items-center gap-3">
                                        <span className="text-lg leading-none">{lang.flag}</span>
                                        <span className="font-medium">{lang.label}</span>
                                    </span>
                                    {currentLang === lang.code && <Check size={14} className="text-white" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent mx-4 my-2" />

                    {/* Currency Section */}
                    <div className="p-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-bold mb-3 px-2 font-body">
                            {t ? t('currency') : 'Currency'}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.entries(CURRENCIES).map(([code, currency]) => (
                                <button
                                    key={code}
                                    onClick={() => { onCurrencyChange(code); setIsOpen(false); }}
                                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-[12px] transition-all duration-300 border ${
                                        currentCurrency === code
                                            ? 'bg-neutral-50 border-neutral-200 text-luxury-charcoal font-bold shadow-soft'
                                            : 'bg-transparent border-transparent text-neutral-500 hover:bg-neutral-50 hover:text-luxury-charcoal'
                                    }`}
                                >
                                    <span className="font-medium">{currency.code}</span>
                                    <span className="text-neutral-400 bg-neutral-100 px-2 py-0.5 rounded-lg text-[11px] font-medium">{currency.symbol}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LanguageCurrencySelector;
